<template>
  <div class="dacb-reply-form">
    <form @submit.prevent="submit">
      <div v-for="(field, index) in editableFields" :key="`field-${index}`">
        <select
          v-if="field.type === 'select'"
          :ref="el => setFieldRef(field.name, el)"
          :class="{ '-error': field.valid === false, '-dirty': field.dirty }"
          @focus="inputFocus"
          @blur="inputBlur"
          @change="selectChange(field)"
        >
          <option value="" disabled selected>{{ field.name }}</option>
          <option v-for="(option, idx) in fieldOptions(field.options)" :key="`option-${idx}`" :value="option">
            {{ option }}
          </option>
        </select>

        <div v-else-if="field.type === 'message'" :ref="el => setFieldRef(field.name, el)" @click="sendMessage(field.name)">
          <div class="dacb-message dacb-suggested">
            <div class="dacb-text" :style="{ 'background-color': '#FFFFFF', color: '#494949' }">
              {{ field.name }}
            </div>
          </div>
        </div>

        <input
          v-else
          :ref="el => setFieldRef(field.name, el)"
          :type="fieldType(field.type, field.name)"
          :name="field.name"
          :placeholder="field.name"
          :value="field.value"
          :class="{ '-error': field.valid === false }"
          @focus="inputFocus"
          @blur="inputBlur"
        />
      </div>

      <action-button
        v-if="
          editableFields.some((field) => {
            return field.type !== 'message';
          })
        "
        text="Submit"
        :state="state"
        :style="{ backgroundColor: options.primaryColour }"
        @pressed="submit"
      ></action-button>
    </form>

    <span class="skip">
      <a href="#" @click.prevent="skip">{{ $t("skip") }}</a>
    </span>
  </div>
</template>

<script>
import ActionButton from "components/ActionButton.vue";
import api from "helpers/api";
import bus from "helpers/bus";
import dig from "object-dig";
import _ from "underscore";
import { mapState } from "vuex";

export default {
  components: { ActionButton },

  props: {
    fields: {
      type: Array,
      default() {
        return [];
      }
    },
    data: {
      type: Object,
      default() {
        return {};
      }
    }
  },

  data() {
    return {
      state: "normal",
      editableFields: [],
      isSkipping: false,
      inputBlurTimeout: null,
      fieldRefs: {}
    };
  },

  computed: {
    ...mapState({
      options: (state) => state.options.options,
      mobileMode: (state) => state.settings.mobileMode
    })
  },

  watch: {
    fields(newValue) {
      this.setEditableFields(newValue);
      this.state = "normal";
    }
  },

  mounted() {
    this.setEditableFields(this.fields, this.data);

    this.$nextTick(() => {
      bus.$emit("messageList:doScroll");
      this.autoFocus();
    });
  },

  methods: {
    setFieldRef(name, el) {
      if (el) {
        this.fieldRefs[name] = el;
      }
    },

    getFieldRef(name) {
      return this.fieldRefs[name];
    },

    fieldType(type, name) {
      if (name === "Contact number") return "tel";
      if (type === "phone_number") return "tel";
      return type;
    },

    autoFocus() {
      const firstField = this.getFieldRef(this.fields[0].name);
      if (firstField) firstField.focus();
    },

    inputFocus() {
      clearTimeout(this.inputBlurTimeout);
      bus.$emit("inputFocus");
      if (this.mobileMode) {
        bus.$emit("messageList:doScroll");
      }
    },

    inputBlur() {
      // use a timeout to prevent the blur event from triggering
      // when switching between inputs
      clearTimeout(this.inputBlurTimeout);
      this.inputBlurTimeout = setTimeout(() => {
        bus.$emit("inputBlur");
      }, 100);
    },

    submit() {
      if (this.state !== "normal") return;
      this.state = "loading";

      this.validateForm().then((valid) => {
        if (!valid) {
          this.state = "normal";
          return;
        }

        const formdata = {};
        _.each(this.fields, (field) => {
          formdata[field.name] = this.getFieldRef(field.name).value;
        });

        this.$store.dispatch("createMessage", { formdata });
      });
    },

    validateForm() {
      let valid = true;
      let pending;
      let val, matches;

      this.editableFields = _.map(this.editableFields, (field, index) => {
        val = this.getFieldRef(field.name).value.trim();
        field.valid = true;
        field.value = val;

        _.each(field.validation, (validation) => {
          switch (validation.type) {
            case "required":
              if (val.length === 0) {
                valid = false;
                field.valid = false;
              }
              break;

            case "regex":
              matches = val.match(new RegExp(validation.value)) || [];
              if (!matches.length) {
                valid = false;
                field.valid = false;
              }
              break;

            case "min_length":
              if (val.length < parseInt(validation.value, 10)) {
                valid = false;
                field.valid = false;
              }
              break;

            case "phone_number":
              pending = {
                xhr: api.instance().post("public/v1/validations/phone", { number: val }),
                fieldIndex: index
              };
              break;
          }
        });

        return field;
      });

      if (pending) {
        return pending.xhr.then((resp) => {
          if (!resp.data.valid) {
            valid = false;
            this.setEditableFieldInvalid(pending.fieldIndex);
          }
          return valid;
        });
      } else {
        return Promise.resolve(valid);
      }
    },

    skip() {
      if (this.isSkipping) return;
      this.isSkipping = true;

      this.$store.dispatch("skipForm");
    },

    fieldOptions(str) {
      return _.map(str.split(","), (opt) => {
        return opt.trim();
      });
    },

    setEditableFields(fields, data) {
      const num = _.last(dig(data, "numbers") || []) || "";
      const email = _.last(dig(data, "emails") || []) || "";

      fields = _.map(fields, (f) => {
        if (f.type === "select") f.dirty = true;
        // Only pre-populate the number field if asking for a number (e.g. not for mileage)
        // TODO: remove this once "phone_number" option added to API, webapp and existing client forms
        if (f.type === "number" && f.name.includes("number")) f.value = num;
        if (f.type === "phone_number") f.value = num;
        if (f.type === "email") f.value = email;
        return f;
      });

      this.editableFields = fields;
    },

    selectChange(field) {
      this.editableFields = _.map(this.editableFields, (f) => {
        if (f === field) f.dirty = false;
        f.value = this.getFieldRef(f.name).value;

        return f;
      });
    },

    setEditableFieldInvalid(fieldIndex) {
      this.editableFields = _.map(this.editableFields, (field, index) => {
        if (index === fieldIndex) field.valid = false;
        return field;
      });
    },

    sendMessage(message) {
      // Send a 'suggested question' directly
      if (this.submitting) return;

      this.buttonText = "Please wait...";
      this.submitting = true;

      // Send the message with a small delay to let the name request
      // complete first
      this.$store
        .dispatch("createMessage", { content: message })
        .catch(() => {
          console.error("error adding message");
        })
        .then(() => {
          this.submitting = false;
          this.buttonText = "Start Chat";
        });
    }
  }
};
</script>

<style>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
</style>
